import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './AdminSidebar.module.css';

import collapsedLogo from '../../../assets/Logo + Icon.png';
import expandedLogo from '../../../assets/covi_standlogo_white.png';
import pinnedLogo from '../../../assets/Covi_pinned_logo.png';
import {
    ArchiveBoxIcon,
    ChartPieIcon,
    CogIcon,
    CurrencyDollarIcon,
    DocumentIcon,
    Squares2X2Icon,
    StarIcon,
    UserGroupIcon,
    UserIcon
} from '@heroicons/react/20/solid';

interface SubMenuItem {
    id: string;
    label: string;
    path: string;
    icon: React.ForwardRefExoticComponent<React.SVGProps<SVGSVGElement>>;
}

interface MenuItem {
    id: string;
    label: string;
    icon: React.ForwardRefExoticComponent<React.SVGProps<SVGSVGElement>>;
    path: string;
    hasState?: boolean;
    subMenus?: SubMenuItem[];
}

// 사이드바 메뉴 아이템 (상수로 분리)
const menuItems: MenuItem[] = [
    {
        id: 'dashboard',
        label: 'Main Dashboard',
        icon: ChartPieIcon,
        path: '/admin',
        hasState: true
    },
    {
        id: 'projects',
        label: 'Projects',
        icon: Squares2X2Icon,
        path: '/admin/projects'
    },
    {
        id: 'clients',
        label: 'Clients',
        icon: UserGroupIcon,
        path: '/admin/clients',
        subMenus: [
            {
                id: 'account',
                label: 'Account',
                path: '/admin/clients/account',
                icon: UserIcon
            },
            {
                id: 'contract',
                label: 'Contract',
                path: '/admin/clients/contract',
                icon: DocumentIcon
            }
        ]
    },
    {
        id: 'documents',
        label: 'Documents',
        icon: ArchiveBoxIcon,
        path: '/admin/documents'
    },
    {
        id: 'billing',
        label: 'Billing',
        icon: CurrencyDollarIcon,
        path: '/admin/billing'
    },
    {
        id: 'settings',
        label: 'Settings',
        icon: CogIcon,
        path: '/admin/settings'
    }
];

interface AdminSidebarProps {
    className?: string;
    onExpand?: (expanded: boolean) => void;
    onToggle?: () => void;
    onMenuClick?: () => void;
    isExpanded?: boolean;
    isLocked?: boolean;
    isMobile?: boolean;
    isInitialized?: boolean;
    isFirstTempLogin?: boolean;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({
                                                       className = '',
                                                       onExpand,
                                                       onToggle,
                                                       onMenuClick,
                                                       isExpanded = false,
                                                       isLocked = false,
                                                       isMobile = false,
                                                       isInitialized = false,
                                                       isFirstTempLogin = false
                                                   }) => {
    const [expandedMenuItem, setExpandedMenuItem] = useState<string | null>(null);
    const [persistentExpandedMenuItem, setPersistentExpandedMenuItem] = useState<string | null>(null);
    const navigate = useNavigate();
    const location = useLocation();

    // 현재 경로에 해당하는 서브메뉴가 있으면 자동으로 열기
    useEffect(() => {
        const currentMenuItem = menuItems.find(item =>
            item.subMenus?.some(subItem => location.pathname === subItem.path)
        );

        if (currentMenuItem) {
            setPersistentExpandedMenuItem(currentMenuItem.id);
            setExpandedMenuItem(currentMenuItem.id);
        }
    }, [location.pathname]);

    // 메뉴 활성 상태 확인
    const isMenuActive = useCallback((path: string) => {
        if (path === '/admin') return location.pathname === path;
        return location.pathname.startsWith(path);
    }, [location.pathname]);

    // 네비게이션 처리
    const handleNavigate = useCallback(async (path: string, hasState?: boolean) => {
        // 모바일에서 메뉴 클릭 시 사이드바 닫기
        onMenuClick?.();

        try {
            const navigationOptions = hasState ? { state: { isFirstTempLogin } } : undefined;
            navigate(path, navigationOptions);
        } catch (error) {
            console.error('Navigation failed:', error);
        }
    }, [navigate, onMenuClick, isFirstTempLogin]);

    // 메뉴 클릭 처리
    const handleMenuClick = useCallback((item: MenuItem) => {
        if (item.subMenus) {
            const newExpandedItem = persistentExpandedMenuItem === item.id ? null : item.id;
            setPersistentExpandedMenuItem(newExpandedItem);
            setExpandedMenuItem(newExpandedItem);

            // PC에서 서브메뉴가 있고 잠금되지 않은 경우, 사이드바를 임시로 잠금
            if (!isExpanded && !isMobile && !isLocked && newExpandedItem) {
                onToggle?.();
            }
        } else {
            handleNavigate(item.path, item.hasState);
        }
    }, [persistentExpandedMenuItem, isExpanded, isMobile, isLocked, onToggle, handleNavigate]);

    // 호버 처리 (PC에서만, 잠금되지 않은 경우에만)
    const handleExpand = useCallback((expanded: boolean) => {
        if (!isMobile && !isLocked) {
            onExpand?.(expanded);

            // 호버 시에도 지속적인 서브메뉴 상태 유지
            if (expanded && persistentExpandedMenuItem) {
                setExpandedMenuItem(persistentExpandedMenuItem);
            } else if (!expanded && persistentExpandedMenuItem) {
                // 사이드바가 닫혀도 persistentExpandedMenuItem은 유지
                setExpandedMenuItem(persistentExpandedMenuItem);
            }
        }
    }, [isMobile, isLocked, onExpand, persistentExpandedMenuItem]);

    // 로고 클릭 처리 (PC에서만 잠금 토글)
    const handleLogoClick = useCallback(() => {
        if (isMobile) return;
        onToggle?.();
    }, [isMobile, isLocked, onToggle]);

    // 사이드바 클래스명 계산
    const sidebarClassName = useMemo(() => {
        const classes = [styles.sidebar];
        if (isExpanded) classes.push(styles.expanded);
        else classes.push(styles.collapsed);
        if (!isInitialized) classes.push(styles.noTransition);
        if (className) classes.push(className);
        return classes.join(' ');
    }, [isExpanded, isInitialized, className]);

    // 로고 클래스명 계산
    const logoClassName = useMemo(() => {
        const classes = [styles.logo];
        if (isLocked) classes.push(styles.locked);
        return classes.join(' ');
    }, [isLocked]);

    // 서브메뉴 표시 조건: 사이드바가 확장되어 있고, 해당 메뉴가 확장되어 있을 때
    const shouldShowSubMenu = useCallback((itemId: string) => {
        return isExpanded && (expandedMenuItem === itemId || persistentExpandedMenuItem === itemId);
    }, [isExpanded, expandedMenuItem, persistentExpandedMenuItem]);

    return (
        <div
            className={sidebarClassName}
            onMouseEnter={() => handleExpand(true)}
            onMouseLeave={() => handleExpand(false)}
        >
            <div
                className={logoClassName}
                onClick={handleLogoClick}
                style={{ cursor: isMobile ? 'default' : 'pointer' }}
                role={isMobile ? undefined : "button"}
                tabIndex={isMobile ? undefined : 0}
                onKeyDown={isMobile ? undefined : (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleLogoClick();
                    }
                }}
                title={isMobile ? undefined : (isLocked ? "사이드바 잠금 해제" : "사이드바 잠금")}
            >
                <img
                    src={isLocked ? pinnedLogo : (isExpanded ? expandedLogo : collapsedLogo)}
                    alt="Logo"
                    className={`${styles.logoImage} ${
                        isLocked ? styles.pinnedLogo :
                            (isExpanded ? styles.expandedLogo : styles.collapsedLogo)
                    }`}
                />
            </div>

            <nav className={styles.navigation}>
                {menuItems.map((item) => (
                    <div key={item.id}>
                        <button
                            onClick={() => handleMenuClick(item)}
                            className={`${styles.menuItem} ${isMenuActive(item.path) ? styles.active : ''}`}
                            aria-expanded={item.subMenus ? persistentExpandedMenuItem === item.id : undefined}
                            title={!isExpanded && !isMobile ? item.label : undefined}
                        >
                            <span className={styles.icon}>
                                <item.icon className={styles.iconImage} />
                            </span>
                            {isExpanded && <span className={styles.label}>{item.label}</span>}
                        </button>
                        {item.subMenus && shouldShowSubMenu(item.id) && (
                            <div className={styles.subMenu} role="menu">
                                {item.subMenus.map((subItem) => (
                                    <button
                                        key={subItem.id}
                                        onClick={() => handleNavigate(subItem.path)}
                                        className={`${styles.subMenuItem} ${
                                            location.pathname === subItem.path ? styles.active : ''
                                        }`}
                                        role="menuitem"
                                    >
                                        <span className={styles.subIcon}>
                                            <subItem.icon className={styles.subIconImage} />
                                        </span>
                                        <span className={styles.subLabel}>{subItem.label}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </nav>

            <div className={styles.userSection}>
                <div className={styles.account}>
                    <span className={styles.icon}>
                        <StarIcon className={styles.iconImage} />
                    </span>
                    {isExpanded && <span className={styles.label}>Admin</span>}
                </div>
            </div>
        </div>
    );
};

export default AdminSidebar;